import React, { memo, useMemo } from 'react'
import { Modal, Typography } from '@mui/material'
import tw, { css } from 'twin.macro'
import { Button } from '../shared/Button'
import { PromoteMinor } from '@shopify/polaris-icons'
import { hasTextContent } from 'src/utils/htmlUtils'

interface Props {
  isOpen: boolean
  cost?: string | null
  coverage?: string | null
  coverageDetails?: string | null
  coverageDetailsPdfUrl?: string | null
  onClose(): void
}

const isSafeUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url)

    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
  } catch {
    return false
  }
}

const GigSafeCoverageModalComponent = ({
  isOpen,
  cost,
  coverage,
  coverageDetails,
  coverageDetailsPdfUrl,
  onClose,
}: Props) => {
  const container = useMemo(() => document.getElementById('root'), [])

  const safePdfUrl = useMemo(
    () =>
      coverageDetailsPdfUrl && isSafeUrl(coverageDetailsPdfUrl) ? coverageDetailsPdfUrl : null,
    [coverageDetailsPdfUrl],
  )

  return (
    <Modal
      container={container}
      open={isOpen}
      onClose={onClose}
      aria-labelledby="gigsafe-coverage-modal-title"
      css={tw`flex justify-center items-end sm:items-center backdrop-blur p-0 h-full`}
      disableScrollLock
    >
      <div css={tw`w-full max-w-[590px] mx-4 flex flex-col p-6 bg-shades-0 rounded-3xl`}>
        <Typography css={tw`mb-6 font-bold text-[20px]`} variant="h2">
          GigSafe coverage details
        </Typography>
        {cost && (
          <div css={tw`mb-4`}>
            <Typography css={tw`text-[#000] text-base mb-1`} variant="h6">
              Cost
            </Typography>
            <Typography css={tw`text-[#847E96] text-base font-normal`}>{cost}</Typography>
          </div>
        )}
        {coverage && (
          <div css={tw`mb-4`}>
            <Typography css={tw`text-[#000] text-base mb-1`} variant="h6">
              Coverage
            </Typography>
            <Typography css={tw`text-[#847E96] text-base font-normal`}>{coverage}</Typography>
          </div>
        )}
        {hasTextContent(coverageDetails) && (
          <div
            css={[tw`mb-4`, styles.modalCopy]}
            dangerouslySetInnerHTML={{ __html: coverageDetails }}
          />
        )}
        {safePdfUrl && (
          <a href={safePdfUrl} target="_blank" rel="noopener noreferrer" css={tw`no-underline`}>
            <Button
              className={tw`w-full mb-3`}
              color="light"
              rightIcon={<PromoteMinor width={20} css={tw`fill-primary-500`} />}
            >
              Learn more
            </Button>
          </a>
        )}
        <Button
          className={tw`w-full`}
          color="grey"
          onClick={onClose}
          data-testid="contractors-onboarding-insurance-coverage-modal-close-btn"
        >
          Close
        </Button>
      </div>
    </Modal>
  )
}

export const GigSafeCoverageModal = memo(GigSafeCoverageModalComponent)

const styles = {
  modalCopy: css`
    p,
    span,
    div,
    * {
      color: #847e96;
      margin: 0;
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-word;
      max-width: 100%;
    }
    p:first-child {
      margin-top: 0;
    }
    p:last-child {
      margin-bottom: 0;
    }
  `,
}
